import {SiFile} from '../models/file.model';
import {NgForm} from '@angular/forms';

export class GenericFunctionsUtil {

  /**
   * Find value in nested object by nested key.
   *
   * Example:
   * const myObject = {
   *   keyA: {
   *     keyAB: 2
   *   }
   * };
   *
   * findInObjectByNestedKey('keyA.keyB', myObject); // Returns 2
   *
   */
  public static findInObjectByNestedKey(path: string | string[], obj: any = self, separator: string = '.'): any {
    const properties = Array.isArray(path) ? path : path.split(separator);
    return properties.reduce((prev, curr) => prev && prev[curr], obj);
  }

  /**
   * Replace arguments in a string.
   *
   * Example:
   * let myText = 'Hello {0} and {1}';
   * myText = stringReplaceArguments(myText, ['Front', 'Back']); // Returns 'Hello Front and Back'
   *
   */
  public static stringReplaceArguments(text: string, params: any[] = []): string {
    for (let i = 0; i < params.length; i++) {
      const regEx = new RegExp('\\{' + (i) + '\\}', 'gm');
      text = text.replace(regEx, params[i].toString());
    }

    return text;
  }

  /**
   * Performs a deep clone of an object.
   * @param obj Object to be cloned.
   *
   * Example:
   *
   * const municipioFilter = {
   *   hola = "caracola",
   *   muniDesc_lar = "encia",
   *   provincia = {
   *     id: 81
   *   }
   * };
   *
   * deepClone(municipioFilter) // Returns municipioFilter but as a new object with a new reference
   */
  public static deepClone(inObject: any): any {
    let outObject: any;
    let value: any;
    let key: string;
    if (typeof inObject !== "object" || inObject === null) {
      return inObject; // Return the value if inObject is not an object
    }

    // Create an array or object to hold the values
    outObject = Array.isArray(inObject) ? [] : {};

    for (key in inObject) {
      value = inObject[key];

      // Recursively (deep) copy for nested objects, including arrays
      outObject[key] = this.deepClone(value);
    }

    return outObject;
  }

  /**
   * Checks if an object has at least one property with an existing value.
   * @param obj Object to be checked.
   *
   * Example:
   *
   * const objectWithNull = {
   *   name: null
   * };
   * checkIfAnyPropertyExists(objectWithNull) // Returns false.
   *
   * const objectWithValue = {
   *   name: 'name'
   * };
   * checkIfAnyPropertyExists(objectWithValue) // Returns true.
   */
  public static checkIfAnyPropertyExists(obj: object): boolean {
    if (!obj || !Object.values(obj).length) {
      return false;
    }

    return Object.values(obj).some(value => {
      if (value !== null && value !== undefined && value !== '') {
        return true;
      }

      return false;
    });
  }

  /**
   * Remove all null/undefined values from an object.
   * @param obj Object to be changed
   *
   * Example:
   *
   * const object = {
   *   name: 'Marcos',
   *   surname1: null,
   *   surname2: undefined
   * }
   *
   * removeNullValuesFromObject(object) // Returns { name: 'Marcos' }
   */
  public static removeNullValuesFromObject(obj: any): any {
    const objCopy = this.deepClone(obj);
    Object.keys(objCopy).forEach(key => {
      if (objCopy[key] === null || objCopy[key] === undefined || (Array.isArray(objCopy[key]) && objCopy[key]?.length === 0)) {
        delete objCopy[key];
      }
    });

    return objCopy;
  }

  public static clearFilters(filters: object, defaultFilters: object): void {
    Object.keys(filters).forEach(key => filters[key] = null);
    setTimeout(() => {
      Object.keys(defaultFilters).forEach(key => filters[key] = {...defaultFilters[key]});
    });
  }

  public static normalizeString(value: string): string {
    if (!value) {
      return value;
    }

    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  public static isSiFile(file: File | SiFile): file is SiFile {
    return !!(file as SiFile).id || !!(file as SiFile).url || !!(file as SiFile).fileId;
  }

  /**
   * Sort an array by an attribute
   *
   * Example:
   * const model = [
   *  {
   *    id: 1,
   *    name: "grupo 1",
   *    order: 2
   *  },
   *  {
   *    id: 2,
   *    name: "grupo 2",
   *    order: 1
   *  }
   * ];
   * sortItems(model, 'order');
   * // Returns the array sorted by the attribute "order".
   *
   */
  public static sortItems(items: any[], sortField: string): void {
    if (!items?.length || !sortField) {
      return;
    }

    items.sort((a, b) => {
      if (a[sortField] > b[sortField]) {
        return 1;
      }
      if (a[sortField] < b[sortField]) {
        return -1;
      }
      return 0;
    });
  }

  /**
   * Convert the date to UTC in ms (taking into account the user's time zone)
   *
   * Example:
   * const dateMs = 1700654400000; // 22/11/2023 13:00 (UTC+1)
   * const dateUTC = userDateToUTCMs(dateMs); // dateUTC = 1700658000000 -> 22/11/2023 13:00 (UTC)
   */
  public static userDateToUTCMs(dateMs: number): number {
    const offsetLocal = new Date(dateMs).getTimezoneOffset();
    return dateMs - (offsetLocal * 60 * 1000);
  }

  /**
   * Convert the date to UTC as Date (taking into account the user's time zone)
   *
   * Example:
   * const dateMs = 1700654400000; // 22/11/2023 13:00 (UTC+1)
   * const dateUTC = userDateToUTCDate(dateMs); // dateUTC = Date[22/11/2023 13:00 (UTC)]
   */
  public static userDateToUTCDate(dateMs: number): Date {
    const date = new Date(dateMs);
    const dateUTC = new Date();
    dateUTC.setUTCFullYear(date.getUTCFullYear());
    dateUTC.setUTCMonth(date.getUTCMonth());
    dateUTC.setUTCDate(date.getUTCDate());
    dateUTC.setUTCHours(date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), 0);
    return dateUTC;
  }

  /**
   * Convert the date to user's time zone date in ms
   *
   * Example:
   * const dateMs = 1700658000000; // 22/11/2023 13:00 (UTC)
   * const dateUserTimeZone = UTCDateToUserDateMs(dateMs); // dateUTC = 1700658000000 -> 22/11/2023 13:00 (UTC+1)
   */
  public static UTCDateToUserDateMs(dateMs: number): number {
    const offsetLocal = new Date(dateMs).getTimezoneOffset();
    return dateMs + (offsetLocal * 60 * 1000);
  }

  /**
   * Mask an e-mail, a phone number or a string
   *
   * getEmailMasked(3, '*', 'test@email.com'); // Returns *est@email.com
   * getPhoneMasked(2, '*', 123456789); // Returns *******89
   * stringMask(3, '$', 'testString'); // Returns $$$$$$$ing
   */

  public static getEmailMasked(margin: number, symbol: string, email: string): string {
    const index = email.indexOf('@');
    const substring1 = email.slice(0, index);
    const substring2 = email.slice(index);
    return this.stringMask(margin, symbol, substring1) + substring2;
  }

  public static getPhoneMasked(margin: number, symbol: string, phone: number | string): string {
    return this.stringMask(margin, symbol, phone.toString());
  }

  public static stringMask(margin: number, symbol: string, str: string): string {
    if (!str) {
      return '';
    }

    const maskStr = str.slice(-margin);
    return maskStr.padStart(str.length, symbol);
  }

  /**
   * Capitalize first letter of a string
   *
   * capitalizeFirstLetter('notification permission'); // Returns: Notification permission
   * capitalizeFirstLetter('p'); // Returns: P
   * capitalizeFirstLetter('&'); // Returns: '&'
   * capitalizeFirstLetter(''); // Returns: ''
   */

  public static capitalizeFirstLetter(str: string): string {
    return str?.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : '';
  }

  /**
   * Compares the content of two arrays and returns true if they have the same elements (regardless of order)
   * @param a string[] | number[]
   * @param b string[] | number[]
   *
   * let array1 = [11, 22, 33];
   * let array2 = [21, 22, 23];
   * let array3 = [11, 33, 22];
   * areSameArraysSimple(array1, array2); // Returns false
   * areSameArraysSimple(array1, array3); // Returns true
   */
  public static areSameArraysSimple(a: any[] = [], b: any[] = []): boolean {
    return a?.length === b?.length && a.every((element) => b.includes(element));
  }

  /**
   * Compares the content of two arrays and returns true if they have the same elements (regardless of order)
   * @param a object[]
   * @param b object[]
   * @param attrToFilter string Attribute of each object we want to compare
   *
   * let array1 = [{id: 1, name: 'building}, {id: 3, name: 'room}];
   * let array2 = [{id: 2, name: 'floor}, {id: 3, name: 'room}];
   * let array3 = [{id: 3, name: 'room}, {id: 1, name: 'building}];
   * areSameArraysObject(array1, array2); // Returns false
   * areSameArraysObject(array1, array3); // Returns true
   */
  public static areSameArraysObject(a: any[] = [], b: any[] = [], attrToFilter: string): boolean {
    const auxA = a.map((element) => element[attrToFilter]);
    const auxB = b.map((element) => element[attrToFilter]);
    return GenericFunctionsUtil.areSameArraysSimple(auxA, auxB);
  }

  public static markAllFieldsOfFormAsDirty(form: NgForm): void {
    Object.values(form.controls).forEach(control => {
      control.markAsDirty();
    });
  }

  /**
   * Calculates and returns the number of active filters from the `filtersApplied` object.
   *
   * A filter is considered active if:
   * - It is an array with at least one element.
   * - Or it is any other "truthy" value (not null, undefined, false, 0, '', etc.).
   *
   * @param filtersApplied - Object containing the applied filters (can be arrays or other value types).
   * @returns Number of active filters.
   */
  public static getActiveFiltersCount(filtersApplied: Record<string, any>): number {
    return Object.values(filtersApplied).filter(field => (Array.isArray(field) ? !!field.length : !!field)).length;
  }
}
